import { useState, useEffect } from "react";
import { ApplicationWithId } from "../interfaces/IApplications";
import { AppUser } from "../interfaces/IUsers";
import { StreetType } from "../interfaces/enums";
import { emptyInvoiceData } from "../utils/emptyObjects";
import { validateInvoiceData } from "../utils/validations";

export interface ApplicationInvoiceFormData {
  isInvoiceNeeded: boolean;
  invoiceName: string;
  invoiceTaxId: string;
  invoiceStreet: string;
  invoiceHouseNumber: string;
  invoiceLocalNumber: string;
  invoicePostalCode: string;
  invoiceCity: string;
  invoiceStreetType: StreetType;
}

export interface ApplicationInvoiceState {
  formData: ApplicationInvoiceFormData;
  formErrors: Record<string, string>;
  isEditing: boolean;
  isSaving: boolean;
}

export interface ApplicationInvoiceActions {
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleEdit: () => void;
  handleCancel: () => void;
  handleSave: () => Promise<boolean>;
}

/**
 * Hook zarządzający stanem formularza faktury dla zgłoszenia
 * 
 * Zasady:
 * - Domyślnie wypełnia danymi z konta użytkownika (jeśli dostępne)
 * - Pozwala na zmianę danych specyficznych dla zgłoszenia
 * - Walidacja tylko przy zapisie
 * - Tryb edycji/readonly
 */
export const useApplicationInvoice = (
  application: ApplicationWithId | null,
  user: AppUser | null,
  onUpdate: (applicationId: string, data: Partial<ApplicationWithId>) => Promise<{ success: boolean; error?: string }>
): ApplicationInvoiceState & ApplicationInvoiceActions => {
  const [formData, setFormData] = useState<ApplicationInvoiceFormData>({
    isInvoiceNeeded: false,
    invoiceName: "",
    invoiceTaxId: "",
    invoiceStreet: "",
    invoiceHouseNumber: "",
    invoiceLocalNumber: "",
    invoicePostalCode: "",
    invoiceCity: "",
    invoiceStreetType: StreetType.STREET,
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [initialFormData, setInitialFormData] = useState<ApplicationInvoiceFormData | null>(null);

  // Inicjalizacja formularza danymi
  useEffect(() => {
    if (!application) return;

    // Priorytet: dane z zgłoszenia, jeśli istnieją
    // Jeśli brak, użyj danych z konta użytkownika
    const invoiceData = application.invoiceData || 
      (user?.isInvoiceNeeded && user.invoiceData ? user.invoiceData : null);

    const newFormData: ApplicationInvoiceFormData = {
      isInvoiceNeeded: application.isInvoiceNeeded || false,
      invoiceName: invoiceData?.name || "",
      invoiceTaxId: invoiceData?.taxId || "",
      invoiceStreet: invoiceData?.street || "",
      invoiceHouseNumber: invoiceData?.houseNumber || "",
      invoiceLocalNumber: invoiceData?.localNumber || "",
      invoicePostalCode: invoiceData?.postalCode || "",
      invoiceCity: invoiceData?.city || "",
      invoiceStreetType: invoiceData?.streetType || StreetType.STREET,
    };

    setFormData(newFormData);
    setInitialFormData(newFormData);
  }, [application, user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    // Wyczyść błąd dla tego pola
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    // Przywróć dane początkowe
    if (initialFormData) {
      setFormData(initialFormData);
    }
    setFormErrors({});
    setIsEditing(false);
  };

  const handleSave = async (): Promise<boolean> => {
    if (!application) return false;

    // Walidacja
    const validation = validateInvoiceData(formData);
    if (!validation.isValid) {
      setFormErrors(validation.errors);
      return false;
    }

    setIsSaving(true);
    try {
      // Przygotuj dane do zapisu
      const updatedData: Partial<ApplicationWithId> = {
        isInvoiceNeeded: formData.isInvoiceNeeded,
        invoiceData: formData.isInvoiceNeeded
          ? {
              name: formData.invoiceName,
              taxId: formData.invoiceTaxId,
              street: formData.invoiceStreet,
              houseNumber: formData.invoiceHouseNumber,
              localNumber: formData.invoiceLocalNumber || "",
              postalCode: formData.invoicePostalCode,
              city: formData.invoiceCity,
              streetType: formData.invoiceStreetType,
            }
          : emptyInvoiceData,
      };

      const result = await onUpdate(application.id, updatedData);
      
      if (result.success) {
        // Zaktualizuj dane początkowe
        setInitialFormData(formData);
        setIsEditing(false);
        setFormErrors({});
        return true;
      } else {
        setFormErrors({ save: result.error || "Nie udało się zapisać danych faktury" });
        return false;
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Wystąpił błąd podczas zapisywania";
      setFormErrors({ save: errorMessage });
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  return {
    formData,
    formErrors,
    isEditing,
    isSaving,
    handleChange,
    handleEdit,
    handleCancel,
    handleSave,
  };
};
